import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";

export type CutoffRow = Pick<
  DatabaseRow<"radiologist_read_cutoffs">,
  "center_user_id" | "date" | "type" | "cutoff_minutes_from_midnight"
>;

export type SpecialtyRow = Pick<
  DatabaseRow<"radiologist_specialties">,
  "center_user_id" | "specialty"
>;

export type CutoffState = {
  type: "available" | "unavailable" | "cutoff_time";
  cutoffMinutesFromMidnight: number | null;
};

// Returns true if a radiologist with this state covers an appointment at `apptMinutes`
export const radiologistCoversSlot = (
  state: CutoffState | undefined,
  apptMinutes: number,
): boolean => {
  if (!state || state.type === "available") return true;
  if (state.type === "unavailable") return false;
  // cutoff_time: available only if appt is before the cutoff
  return (
    state.cutoffMinutesFromMidnight !== null &&
    apptMinutes < state.cutoffMinutesFromMidnight
  );
};

// Build map: userId → dateStr → CutoffState
export const buildCutoffMap = (
  cutoffs: CutoffRow[],
): Record<string, Record<string, CutoffState>> =>
  cutoffs.reduce<Record<string, Record<string, CutoffState>>>(
    (map, { center_user_id, date, type, cutoff_minutes_from_midnight }) => ({
      ...map,
      [center_user_id]: {
        ...(map[center_user_id] ?? {}),
        [date]: {
          type: type as CutoffState["type"],
          cutoffMinutesFromMidnight: cutoff_minutes_from_midnight,
        },
      },
    }),
    {},
  );

// Build map: specialty → userId[]
export const buildSpecialtyToUsersMap = (
  specialties: SpecialtyRow[],
): Record<string, string[]> =>
  specialties.reduce<Record<string, string[]>>(
    (map, { specialty, center_user_id }) => ({
      ...map,
      [specialty]: [...(map[specialty] ?? []), center_user_id],
    }),
    {},
  );

// Returns true if at least one radiologist per required specialty covers the slot
export const isAllSpecialtiesCovered = (
  requiredSpecialties: string[],
  specialtyToUsers: Record<string, string[]>,
  cutoffMap: Record<string, Record<string, CutoffState>>,
  dateStr: string,
  apptMinutes: number,
): boolean =>
  requiredSpecialties.every((specialty) => {
    const userIds = specialtyToUsers[specialty] ?? [];
    return userIds.some((userId) =>
      radiologistCoversSlot(cutoffMap[userId]?.[dateStr], apptMinutes),
    );
  });
